// Utility functions for consistent EST timezone handling across the application
import { format, differenceInDays, differenceInHours, differenceInMinutes } from 'date-fns';

// EST timezone identifier
export const EST_TIMEZONE = 'America/New_York';

/**
 * Formats a date string or Date object to EST timezone with a specific format
 */
export const formatToEST = (
  date: string | Date, 
  formatString: string = 'MMM dd, yyyy \'at\' h:mm a \'EST\''
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  // Convert to EST using toLocaleString
  const estDate = new Date(dateObj.toLocaleString("en-US", { timeZone: EST_TIMEZONE }));
  return format(estDate, formatString);
};

/**
 * Formats auction date and time in EST
 */
export const formatAuctionDate = (date: string | Date): string => {
  return formatToEST(date, 'EEEE, MMMM dd, yyyy \'at\' h:mm a \'EST\'');
};

/**
 * Formats auction date only (no time) in EST
 */
export const formatAuctionDateOnly = (date: string | Date): string => {
  return formatToEST(date, 'MMMM dd, yyyy');
};

/**
 * Formats auction time only in EST
 */
export const formatAuctionTime = (date: string | Date): string => {
  return formatToEST(date, 'h:mm a \'EST\'');
};

/**
 * Get current date/time in EST
 */
export const nowInEST = (): Date => {
  return new Date(new Date().toLocaleString("en-US", { timeZone: EST_TIMEZONE }));
};

/**
 * Convert any date to EST timezone
 */
export const toEST = (date: string | Date): Date => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Date(dateObj.toLocaleString("en-US", { timeZone: EST_TIMEZONE }));
};

/**
 * Format date for auction cards
 */
export const formatAuctionCardDate = (startDate: string | Date, endDate?: string | Date | null): string => {
  const start = formatAuctionDateOnly(startDate);
  if (endDate) {
    const end = formatAuctionDateOnly(endDate);
    if (start === end) {
      // Same day auction
      return `${start} • ${formatAuctionTime(startDate)} - ${formatAuctionTime(endDate)}`;
    } else {
      // Multi-day auction
      return `${start} - ${end}`;
    }
  }
  return `${start} • ${formatAuctionTime(startDate)}`;
};

/**
 * Check if an auction is currently live based on EST times
 */
export const isAuctionLive = (startDate: string | Date, endDate?: string | Date | null): boolean => {
  const now = nowInEST();
  const start = toEST(startDate);
  const end = endDate ? toEST(endDate) : null;
  
  return now >= start && (!end || now <= end);
};

/**
 * Check if an auction is upcoming based on EST times
 */
export const isAuctionUpcoming = (startDate: string | Date): boolean => {
  const now = nowInEST();
  const start = toEST(startDate);
  
  return now < start;
};

/**
 * Check if an auction has ended based on EST times
 */
export const isAuctionEnded = (endDate?: string | Date | null): boolean => {
  if (!endDate) return false;
  
  const now = nowInEST();
  const end = toEST(endDate);
  
  return now > end;
};

/**
 * Format countdown display for upcoming auctions
 */
export const formatCountdown = (startDate: string | Date): string => {
  const now = nowInEST();
  const start = toEST(startDate);
  
  if (start <= now) return 'Starting now';
  
  const days = differenceInDays(start, now);
  const hours = differenceInHours(start, now) % 24;
  const minutes = differenceInMinutes(start, now) % 60;
  
  if (days > 0) {
    return `${days} day${days !== 1 ? 's' : ''}, ${hours} hour${hours !== 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}, ${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
};

/**
 * Format interview dates consistently in EST
 */
export const formatInterviewDate = (date: string | Date): string => {
  return formatToEST(date, 'EEEE, MMMM dd, yyyy \'at\' h:mm a \'EST\'');
};

/**
 * Format time only with EST (12-hour format)
 */
export const formatTimeOnlyEST = (date: string | Date): string => {
  return formatToEST(date, 'h:mm a');
};

/**
 * Format date for display in tables/lists
 */
export const formatDateForDisplay = (date: string | Date): string => {
  return formatToEST(date, 'MMM d, yyyy');
};

/**
 * Format date and time for display
 */
export const formatDateTimeForDisplay = (date: string | Date): string => {
  return formatToEST(date, 'MMM d, yyyy \'at\' h:mm a \'EST\'');
};

/**
 * Format currency amount
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Format number with commas
 */
export const formatNumber = (num: number): string => {
  return num.toLocaleString('en-US');
};

/**
 * Convert a UTC date to datetime-local input format in EST
 * Returns format: "YYYY-MM-DDTHH:mm" which is what datetime-local inputs expect
 */
export const formatForDateTimeInput = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Get date parts in EST timezone
  const estString = dateObj.toLocaleString("en-US", { 
    timeZone: EST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  // Parse "MM/DD/YYYY, HH:mm" format and convert to "YYYY-MM-DDTHH:mm"
  const [datePart, timePart] = estString.split(', ');
  const [month, day, year] = datePart.split('/');
  
  // Handle edge case where hour might be "24" at midnight (some browsers return this)
  let normalizedTime = timePart;
  if (timePart.startsWith('24:')) {
    normalizedTime = '00:' + timePart.slice(3);
  }
  
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${normalizedTime}`;
};

/**
 * Parse a datetime-local input string as EST time and convert to UTC ISO string
 * Input format: "YYYY-MM-DDTHH:mm" (what datetime-local provides)
 * Output format: ISO 8601 UTC string for database storage
 */
export const parseESTToUTC = (dateTimeLocalString: string): string => {
  if (!dateTimeLocalString) return '';
  
  // Parse the datetime-local string components
  const [datePart, timePart] = dateTimeLocalString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  
  // Create a date string that we can parse as EST
  // Format: "YYYY-MM-DD HH:mm:ss" in EST
  const estDateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
  
  // Create date object - JavaScript will interpret this as local time
  // We need to calculate the offset to EST and adjust
  const localDate = new Date(estDateString);
  
  // Get the EST offset for this specific date (handles DST)
  const estFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: EST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  // Get UTC offset for EST timezone at this date
  // EST is UTC-5, EDT is UTC-4
  const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
  const estParts = estFormatter.formatToParts(utcDate);
  const estHour = parseInt(estParts.find(p => p.type === 'hour')?.value || '0');
  const utcHour = utcDate.getUTCHours();
  
  // Calculate the offset (negative because EST is behind UTC)
  let offset = utcHour - estHour;
  if (offset < -12) offset += 24;
  if (offset > 12) offset -= 24;
  
  // Create the final UTC date by adding the offset to the input time
  const finalUTC = new Date(Date.UTC(year, month - 1, day, hours + offset, minutes, 0));
  
  return finalUTC.toISOString();
};